import React, { useMemo, useRef, useState } from "react";
import api from "../../services/api";
import * as XLSX from "xlsx";
import Papa from "papaparse";
import { useAuth } from "../../contexts/AuthContext";
import { formatInUserTimezone } from "../../utils/timezone";
import "../../styles/BulkImport.css";

const normalizeRole = (role) =>
  String(role || "")
    .trim()
    .toLowerCase();
const OWNER_ROLES = ["admin", "sm", "manager", "sales owner", "sales manager"];
const SR_ROLES = ["sr", "sales representative", "sales rep"];

const FIELD_OPTIONS = [
  { value: "company", label: "Dealership" },
  { value: "leadType", label: "Lead Type" },
  { value: "agentName", label: "Sales Representative" },
  { value: "contactFirstName", label: "Contact First Name" },
  { value: "contactLastName", label: "Contact Last Name" },
  { value: "contactEmail", label: "Contact Email" },
  { value: "contactTitle", label: "Contact Job Title" },
  { value: "contactHomePhone", label: "Contact Home Phone" },
  { value: "contactMobilePhone", label: "Contact Mobile Phone" },
  { value: "contactWorkPhone", label: "Contact Work Phone" },
  { value: "state", label: "Dealership State" },
  { value: "email", label: "Dealership Email" },
  { value: "streetAddress", label: "Street Address" },
  { value: "city", label: "City" },
  { value: "zipCode", label: "ZIP" },
  { value: "contactDate", label: "Contact Date" },
  { value: "activityType", label: "Activity" },
  { value: "activityDate", label: "Activity Date" },
  { value: "activityDescription", label: "Activity Notes" },
  { value: "nextActivity", label: "Next Activity" },
  { value: "nextActivityDate", label: "Next Activity Date" },
  { value: "nextActivityNotes", label: "Next Activity Notes" },
  { value: "status", label: "Status" },
  { value: "opportunityYn", label: "Opportunity Y/N" },
  { value: "products", label: "Products" },
  { value: "dmsProviders", label: "DMS Providers" },
  { value: "serviceDashboard", label: "Service Dashboard" },
  { value: "leadSource", label: "Lead Source" },
  { value: "nextSteps", label: "Next Steps" },
  { value: "notes", label: "Notes" },

  // Other supported CRM fields retained for manual mapping
  // Other supported CRM fields retained for manual mapping
  { value: "dealerGroup", label: "Dealer Group" },
  { value: "otherDmsProvider", label: "Other DMS Provider" },
  { value: "manufacturer", label: "Manufacturers" },
  { value: "manufacturer", label: "Manufacturer" },
  { value: "rooftops", label: "# of Rooftops" },
  { value: "mainLine", label: "Dealership Main Line" },
  { value: "website", label: "Dealership Website" },
  { value: "country", label: "Country" },
];

const ALIASES = {
  leadType: ["leadtype"],
  agentName: ["salesrepresentative", "agentsname", "agentname"],
  state: ["dealershipstate", "state", "st"],

  contactFirstName: ["contactfirstname", "firstname"],
  contactLastName: ["contactlastname", "lastname"],
  contactEmail: ["contactemail", "emailaddress"],
  contactTitle: ["contactjobtitle", "jobtitle"],
  contactHomePhone: ["contacthomephone", "phonehome", "phonehome*"],
  contactMobilePhone: ["contactmobilephone", "phonemobile"],
  contactWorkPhone: ["contactworkphone", "phonework"],

  company: ["dealership", "company", "dealershipname", "dealershipname*"],
  streetAddress: [
    "streetaddress",
    "streetaddressline1home",
    "streetaddressline1home*",
  ],
  city: ["city", "cityhome"],
  zipCode: ["zip", "zipcode"],

  contactDate: ["contactdate"],

  activityType: ["activity", "activitytype"],
  activityDate: ["activitydate"],
  activityDescription: ["activitynotes", "activitydescription", "activitydesc"],

  nextActivity: ["nextactivity", "nextcontact"],
  nextActivityDate: ["nextactivitydate", "nextcontactdate", "planneddate"],
  nextActivityNotes: ["nextactivitynotes", "nextcontactnotes"],

  dmsProviders: [
    "dmsproviders",
    "choosedmstype",
    "dmsprovider",
    "dmsprovider*",
  ],
  serviceDashboard: ["chooseservicedashboard"],
  status: ["status", "updatestatususedropdown"],
  opportunityYn: ["opportunityyn"],
  products: ["products", "selecttheappstheyareinterestedin"],
  nextSteps: ["nextsteps", "identifynextsteps"],
  leadSource: ["leadsource"],
  dealerGroup: ["dealergroup"],
  otherDmsProvider: ["otherdmsprovider", "otherdms"],
  manufacturer: ["manufacturer", "manufacturers"],
  rooftops: ["ofrooftops", "rooftops"],
  mainLine: ["mainline"],
  email: ["dealershipemail", "email"],
  website: ["website", "dealershipwebsite"],
  country: ["country"],
  notes: ["notes"],
};

const normalizeHeader = (value) =>
  String(value || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "");
const arrayFields = new Set(["dmsProviders", "products", "manufacturer"]);
const masterOverrideFields = new Set([
  "leadSource",
  "leadType",
  "serviceDashboard",
  "status",
  "opportunityYn",
  "dmsProviders",
  "products",
]);

function autoMap(headers) {
  const mapping = {};
  headers.forEach((header) => {
    const key = normalizeHeader(header);
    const match = FIELD_OPTIONS.find((field) =>
      (ALIASES[field.value] || []).includes(key),
    );
    if (match) mapping[header] = match.value;
  });
  return mapping;
}

function splitArray(value) {
  if (Array.isArray(value)) return value;
  if (value === undefined || value === null || String(value).trim() === "")
    return [];
  return String(value)
    .split(/[,;|]/)
    .map((x) => x.trim())
    .filter(Boolean);
}

function BulkImport({ onComplete, onCancel }) {
  const { user } = useAuth();
  const role = normalizeRole(user?.role);
  const isAdmin = role === "admin";
  const isOwner = OWNER_ROLES.includes(role);
  const isSalesRep = SR_ROLES.includes(role);
  const canSelectAgent = isOwner;

  const [file, setFile] = useState(null);
  const [allRows, setAllRows] = useState([]);
  const [previewData, setPreviewData] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [mapping, setMapping] = useState({});
  const [overrides, setOverrides] = useState({});
  const [options, setOptions] = useState({
    leadSources: [],
    leadTypes: [],
    dmsProviders: [],
    serviceDashboards: [],
    leadStatuses: [],
    products: [],
    salesReps: [],
  });
  const [salesAgentId, setSalesAgentId] = useState(
    user?.userId ? String(user.userId) : "",
  );
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [errors, setErrors] = useState([]);
  const [summary, setSummary] = useState(null);
  const [validationRows, setValidationRows] = useState([]);
  const [ignoredRows, setIgnoredRows] = useState(new Set());
  const [agentOverrides, setAgentOverrides] = useState({});
  const [nextActivityConflicts, setNextActivityConflicts] = useState({});
  const [step, setStep] = useState("upload");
  const fileInputRef = useRef(null);

  const mappedFields = useMemo(
    () => Object.values(mapping).filter(Boolean),
    [mapping],
  );
  const validationErrors = useMemo(
    () => validationRows.filter((row) => !row.valid),
    [validationRows],
  );
  const unhandledErrors = useMemo(
    () => validationErrors.filter((row) => !ignoredRows.has(row.row)),
    [validationErrors, ignoredRows],
  );

  const loadOptions = async () => {
    try {
      const data = await api.getImportOptions();
      setOptions(data || {});
      const currentId = String(user?.userId || "");
      if (currentId) setSalesAgentId(currentId);
    } catch (error) {
      setErrors([
        error.response?.data?.error ||
          error.message ||
          "Failed to load import master data",
      ]);
    }
  };

  const parseFile = (selectedFile) => {
    const reader = new FileReader();
    const done = (data, fields) => {
      if (!fields?.length) {
        setErrors(["The file does not contain a header row."]);
        return;
      }
      if (!data.length) {
        setErrors(["The file does not contain any data rows."]);
        return;
      }
      if (data.length > 500) {
        setErrors([
          `The file contains ${data.length} records. Maximum is 500 records per import.`,
        ]);
        return;
      }
      const nextMapping = autoMap(fields);
      setAllRows(data);
      setPreviewData(data.slice(0, 5));
      setHeaders(fields);
      setMapping(nextMapping);
      setOverrides({});
      setValidationRows([]);
      setIgnoredRows(new Set());
      setAgentOverrides({});
      setNextActivityConflicts({});
      setSummary(null);
      setErrors([]);
      setStep("mapping");
      loadOptions();
    };
    if (/\.csv$/i.test(selectedFile.name)) {
      reader.onload = (e) => {
        const result = Papa.parse(e.target.result, {
          header: true,
          skipEmptyLines: true,
        });
        if (result.errors.length) {
          setErrors(result.errors.map((x) => x.message));
          return;
        }
        done(result.data, result.meta.fields);
      };
      reader.readAsText(selectedFile);
    } else {
      reader.onload = (e) => {
        try {
          const workbook = XLSX.read(new Uint8Array(e.target.result), {
            type: "array",
            cellDates: true,
          });
          const sheet = workbook.Sheets[workbook.SheetNames[0]];
          const data = XLSX.utils.sheet_to_json(sheet, { defval: "" });
          const fields =
            XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "" })[0] || [];
          done(data, fields);
        } catch (error) {
          setErrors([error.message || "Failed to read Excel file"]);
        }
      };
      reader.readAsArrayBuffer(selectedFile);
    }
  };

  const handleFileSelect = (e) => {
    const selected = e.target.files?.[0];
    if (!selected) return;
    if (!/\.(csv|xls|xlsx)$/i.test(selected.name)) {
      setErrors(["Please upload a CSV or Excel file."]);
      return;
    }
    setFile(selected);
    parseFile(selected);
  };

  const handleMappingChange = (header, field) => {
    setMapping((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((key) => {
        if (key !== header && next[key] === field && field) next[key] = "";
      });
      next[header] = field;
      return next;
    });
  };

  const setOverride = (field, value) =>
    setOverrides((prev) => ({ ...prev, [field]: value }));

  const buildRows = () =>
    allRows.map((row) => {
      const lead = {};
      Object.entries(mapping).forEach(([header, field]) => {
        if (
          !field ||
          row[header] === undefined ||
          row[header] === null ||
          row[header] === ""
        )
          return;
        lead[field] = arrayFields.has(field)
          ? splitArray(row[header])
          : row[header];
      });
      if (
        lead.contactFirstName ||
        lead.contactLastName ||
        lead.contactEmail ||
        lead.contactTitle ||
        lead.contactHomePhone ||
        lead.contactMobilePhone ||
        lead.contactWorkPhone
      ) {
        lead.contacts = [
          {
            firstName: lead.contactFirstName || "",
            lastName: lead.contactLastName || "",
            email: lead.contactEmail || "",
            title: lead.contactTitle || "",
            homePhone: lead.contactHomePhone || "",
            mobilePhone: lead.contactMobilePhone || "",
            workPhone: lead.contactWorkPhone || "",
          },
        ];
      }
      return lead;
    });

  const validateBeforeImport = async () => {
    if (!mappedFields.includes("company")) {
      setErrors(["Company / Dealership Name is mandatory."]);
      return;
    }
    if (isOwner && !salesAgentId) {
      setErrors(["Sales Agent is mandatory."]);
      return;
    }

    setImporting(true);
    setProgress(10);
    setErrors([]);
    setStep("validating");
    try {
      const result = await api.validateBulkImportLeads({
        leads: buildRows(),
        mapping,
        overrides,
        salesAgentId: canSelectAgent ? Number(salesAgentId) : null,
      });
      setValidationRows(result.rows || []);
      setIgnoredRows(new Set());

      const seedConflicts = {};
      for (const r of result.rows || []) {
        if (r.conflict?.type === "NEXT_ACTIVITY_EXISTS") {
          seedConflicts[r.row] = "skip";
        }
      }
      setNextActivityConflicts(seedConflicts);

      setProgress(100);
      setStep("validation");
    } catch (error) {
      setStep("mapping");
      setErrors([
        error.response?.data?.error ||
          error.message ||
          "Bulk import validation failed",
      ]);
    } finally {
      setImporting(false);
    }
  };

  const toggleIgnore = (rowNum) => {
    setIgnoredRows((prev) => {
      const next = new Set(prev);
      if (next.has(rowNum)) next.delete(rowNum);
      else next.add(rowNum);
      return next;
    });
  };
  const discardInvalidRows = () => {
    setIgnoredRows(new Set(validationErrors.map((row) => row.row)));
  };

  const setAgentOverride = (rowNum, agentId) => {
    setAgentOverrides((prev) => ({
      ...prev,
      [rowNum]: agentId ? Number(agentId) : "",
    }));
  };

  const isAgentError = (row) =>
    row.errors?.some((error) =>
      String(error).toLowerCase().includes("sales agent"),
    );

  const hasBlockingNonAgentErrors = (row) =>
    !row.valid && !isAgentError(row) && !ignoredRows.has(row.row);
  const blockingErrors = validationErrors.filter(hasBlockingNonAgentErrors);

  const cancelImport = () => {
    setImporting(false);
    setStep("upload");
    setValidationRows([]);
    setIgnoredRows(new Set());
    setAgentOverrides({});
    setNextActivityConflicts({});
    setErrors([]);
  };

  const startImport = async () => {
    if (blockingErrors.length) return;
    const rows = buildRows();
    setImporting(true);
    setProgress(5);
    setErrors([]);
    setStep("importing");
    try {
      const nextActivityOverrides = {};
      for (const [rowNum, decision] of Object.entries(nextActivityConflicts)) {
        nextActivityOverrides[rowNum] = decision;
      }

      const result = await api.bulkImportLeads({
        leads: rows,
        fileName: file?.name || "unknown",
        mapping,
        overrides,
        agentOverrides,
        nextActivityOverrides,
        salesAgentId: canSelectAgent ? Number(salesAgentId) : null,
        ignoredRows: Array.from(ignoredRows),
      });
      if (result?.importId) await pollImportStatus(result.importId);
      else {
        setProgress(100);
        setSummary(result);
        setStep("done");
      }
    } catch (error) {
      setStep("validation");
      setErrors([
        error.response?.data?.error || error.message || "Import failed",
      ]);
    } finally {
      setImporting(false);
    }
  };

  const pollImportStatus = async (id) => {
    const started = Date.now();
    while (Date.now() - started < 10 * 60 * 1000) {
      const status = await api.getImportStatus(id);
      const total = Number(
        status?.total_records || status?.total || allRows.length || 1,
      );
      const processed = Number(status?.processed || status?.imported || 0);
      setProgress(
        Math.min(99, Math.max(5, Math.round((processed / total) * 100))),
      );
      if (
        ["Completed", "Completed With Errors", "Failed", "Cancelled"].includes(
          status?.status,
        )
      ) {
        const summaryResult = {
          importId: id,
          total,
          imported: Number(status.imported || 0),
          failed: Number(status.failed || 0),
          skipped: Number(status.skipped || 0),
          createdUsers: Number(status.createdUsers || 0),
          errors: Array.isArray(status.errors) ? status.errors : [],
        };

        setProgress(100);
        setSummary(summaryResult);
        setStep("done");

        return summaryResult;
      }
      await new Promise((resolve) => setTimeout(resolve, 700));
    }
    throw new Error(
      "Bulk import is taking longer than expected. Please check the import history for the final status.",
    );
  };

  const downloadFailedRecords = () => {
    if (!summary?.errors?.length) return;
    const csv = [
      "Excel Row,Reason",
      ...summary.errors.map(
        (item) =>
          `${JSON.stringify(item.row ?? "")},${JSON.stringify(item.error || "")}`,
      ),
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `lead_import_failed_${summary.importId || "errors"}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadTemplate = async () => {
    try {
      const template = await api.getImportTemplate();
      const ws = XLSX.utils.aoa_to_sheet([
        template.headers,
        template.example || [],
      ]);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Leads");
      XLSX.writeFile(wb, "lead_import_template.xlsx");
    } catch (error) {
      setErrors([error.response?.data?.error || "Failed to download template"]);
    }
  };

  const masterValues = (field) => {
    if (field === "leadSource") return options.leadSources || [];
    if (field === "leadType") return options.leadTypes || [];
    if (field === "serviceDashboard") return options.serviceDashboards || [];
    if (field === "status") return options.leadStatuses || [];
    if (field === "dmsProviders") return options.dmsProviders || [];
    if (field === "products") return options.products || [];
    if (field === "opportunityYn") return [{ name: "Yes" }, { name: "No" }];
    return [];
  };

  const overrideControl = (field) => {
    if (!masterOverrideFields.has(field)) return null;
    const values = masterValues(field);
    const isMulti = field === "dmsProviders" || field === "products";
    if (isMulti) {
      const selected = Array.isArray(overrides[field]) ? overrides[field] : [];
      const label = field === "dmsProviders" ? "DMS Providers" : "Products";
      return (
        <div className="bulk-override-box">
          <div className="bulk-multi-select" aria-label={`${label} selection`}>
            <div className="bulk-multi-select-title">
              <span>
                {selected.length
                  ? `${selected.length} selected`
                  : `Select ${label}`}
              </span>
              <span className="bulk-multi-select-count">
                {selected.length || 0}
              </span>
            </div>
            <div className="bulk-multi-select-options">
              {values.map((v) => {
                const name = typeof v === "string" ? v : v.name;
                const id = typeof v === "string" ? v : v.id || v.name;
                const checked = selected.includes(name);
                return (
                  <label key={id} className="bulk-checkbox-option">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() =>
                        setOverride(
                          field,
                          checked
                            ? selected.filter((x) => x !== name)
                            : [...selected, name],
                        )
                      }
                    />
                    <span>{name}</span>
                  </label>
                );
              })}
            </div>
            <div className="bulk-multi-help">
              Optional override. Leave all unchecked to use the XLS value.
            </div>
          </div>
        </div>
      );
    }
    return (
      <div className="bulk-override-box">
        <select
          className="form-control bulk-override-select"
          value={overrides[field] || ""}
          onChange={(e) => setOverride(field, e.target.value)}
        >
          <option value="">-- Use Excel value --</option>
          {values.map((v) => {
            const name = typeof v === "string" ? v : v.name;
            const id = typeof v === "string" ? v : v.id || v.name;
            return (
              <option key={id} value={name}>
                {name}
              </option>
            );
          })}
        </select>
      </div>
    );
  };

  const wizardSteps = [
    ["upload", "Upload"],
    ["mapping", "Map Columns"],
    ["validation", "Validate"],
    ["importing", "Import"],
    ["done", "Summary"],
  ];
  const wizardOrder = wizardSteps.map((x) => x[0]);
  const currentWizardIndex = wizardOrder.indexOf(step);
  const isWizardVisible = step !== "validating";

  const goBack = () => {
    if (importing) return;
    if (step === "mapping") setStep("upload");
    else if (step === "validation") setStep("mapping");
    else if (step === "done") setStep("validation");
  };

  const clearFileAndStartOver = () => {
    if (importing) return;
    setFile(null);
    setAllRows([]);
    setPreviewData([]);
    setHeaders([]);
    setMapping({});
    setOverrides({});
    setValidationRows([]);
    setIgnoredRows(new Set());
    setAgentOverrides({});
    setNextActivityConflicts({});
    setSummary(null);
    setErrors([]);
    setProgress(0);
    setStep("upload");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  return (
    <div className="bulk-import-overlay">
      <div
        className="bulk-import-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Bulk Import Leads"
      >
        <div className="bulk-import-header">
          <h3>
            <i className="fas fa-upload" style={{ marginRight: 8 }}></i> Bulk
            Import Leads
          </h3>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onCancel}
            disabled={importing}
            aria-label="Close"
          >
            <i className="fas fa-times"></i>
          </button>
        </div>

        {isWizardVisible && (
          <div className="bulk-wizard">
            {wizardSteps.map(([key, label], index) => {
              const completed = currentWizardIndex > index;
              const active = key === step;
              return (
                <React.Fragment key={key}>
                  <div
                    className={`bulk-wizard-step ${active ? "active" : ""} ${completed ? "completed" : ""}`}
                  >
                    <div className="bulk-wizard-node">
                      {completed ? <i className="fas fa-check"></i> : index + 1}
                    </div>
                    <div className="bulk-wizard-label">{label}</div>
                  </div>
                  {index < wizardSteps.length - 1 && (
                    <div className="bulk-wizard-line"></div>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        )}

        <div className="bulk-import-body">
          {step === "upload" && (
            <div className="bulk-upload-panel">
              <h4 className="bulk-section-title">Upload Lead File</h4>
              <p className="bulk-section-desc">
                Upload a CSV or Excel file. The next step lets you review and
                select the CRM field for every XLS column.
              </p>
              <div className="bulk-import-warning" role="note" aria-label="Bulk import date requirements">
                <i className="fas fa-exclamation-triangle" aria-hidden="true"></i>
                <p>
                  <strong>Important:</strong> Activity and Activity Date must be
                  provided together. Next Activity and Next Activity Date must
                  also be provided together. If you fill in either field in a
                  pair, the other is required.
                </p>
              </div>
              <div
                className="drop-zone"
                onClick={() => fileInputRef.current?.click()}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  if (e.dataTransfer.files?.length)
                    handleFileSelect({
                      target: { files: e.dataTransfer.files },
                    });
                }}
              >
                <i
                  className="fas fa-cloud-upload-alt"
                  style={{ fontSize: "3rem" }}
                ></i>
                <p>Drag & drop your file here, or click to browse</p>
                <span className="text-muted">
                  Supports .csv, .xls, .xlsx — maximum 500 records
                </span>
              </div>
              {file && (
                <p style={{ marginTop: 14 }}>
                  Selected: <strong>{file.name}</strong>
                </p>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.xls,.xlsx"
                onChange={handleFileSelect}
                style={{ display: "none" }}
              />
              <p style={{ marginTop: 18 }}>
                <button
                  type="button"
                  className="btn btn-link"
                  onClick={downloadTemplate}
                >
                  Download standard template
                </button>
              </p>
              {errors.length > 0 && (
                <div className="bulk-error-banner">
                  {errors.map((e, i) => (
                    <div key={i}>{typeof e === "string" ? e : e.error}</div>
                  ))}
                </div>
              )}
            </div>
          )}

          {step === "mapping" && (
            <div className="bulk-import-mapping">
              <h4 className="bulk-section-title">
                Map XLS Columns to CRM Fields
              </h4>
              <p className="bulk-section-desc">
                Select the CRM field for each XLS column. DMS Provider is
                optional. Products and DMS Provider support multiple selections
                for CRM overrides.
              </p>

              <div className="bulk-agent-panel">
                <label>
                  Sales Agent <span className="required">*</span>
                </label>
                <select
                  className="form-control bulk-agent-select"
                  value={salesAgentId}
                  onChange={(e) => setSalesAgentId(e.target.value)}
                  disabled={!canSelectAgent}
                >
                  <option value="">-- Select Sales Agent --</option>
                  {(options.salesReps || []).map((rep) => (
                    <option key={rep.user_id} value={rep.user_id}>
                      {rep.full_name}
                      {rep.role ? ` (${rep.role})` : ""}
                    </option>
                  ))}
                </select>
                <small className="text-muted">
                  {isAdmin
                    ? "Admin: current user and all active CRM users are available."
                    : isSalesRep
                      ? "Sales Rep: the logged-in user is selected automatically and cannot be changed."
                      : "Sales Manager/Owner: current user and all active CRM users are available."}
                </small>
              </div>

              <div className="bulk-mapping-card">
                <div className="bulk-mapping-head">
                  <div>XLS Column</div>
                  <div>CRM Field</div>
                  <div>CRM Override</div>
                </div>
                {headers.map((header) => (
                  <div className="bulk-mapping-row" key={header}>
                    <div className="bulk-mapping-header">{header}</div>
                    <div>
                      <select
                        className="bulk-column-select"
                        value={mapping[header] || ""}
                        onChange={(e) =>
                          handleMappingChange(header, e.target.value)
                        }
                      >
                        <option value="">-- Skip this column --</option>
                        {FIELD_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      {overrideControl(mapping[header]) || (
                        <div className="bulk-no-override">
                          Select a CRM field to configure an optional override.
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {previewData.length > 0 && (
                <div className="mapping-preview">
                  <h5>Preview (first 5 of {allRows.length})</h5>
                  <div className="table-wrapper">
                    <table>
                      <thead>
                        <tr>
                          {headers.map((h) => (
                            <th key={h}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {previewData.map((row, i) => (
                          <tr key={i}>
                            {headers.map((h) => (
                              <td key={`${i}-${h}`}>
                                {String(row[h] ?? "").slice(0, 40)}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              {errors.length > 0 && (
                <div className="bulk-error-banner">
                  {errors.map((e, i) => (
                    <div key={i}>{typeof e === "string" ? e : e.error}</div>
                  ))}
                </div>
              )}
            </div>
          )}

          {step === "validating" && (
            <div className="bulk-import-progress">
              <div className="progress-bar">
                <div
                  className="progress-fill"
                  style={{ width: `${progress}%` }}
                ></div>
              </div>
              <p>
                Validating {allRows.length} records. No records are being
                imported yet...
              </p>
            </div>
          )}

          {step === "validation" && (
            <div className="bulk-import-validation">
              <div className="validation-header">
                <div>
                  <h4>Validation Results</h4>
                  <p>
                    {validationRows.filter((r) => r.valid).length} valid ·{" "}
                    {validationErrors.length} with errors · {ignoredRows.size}{" "}
                    ignored
                  </p>
                </div>
              </div>
              {validationErrors.length === 0 ? (
                <div className="validation-success">
                  <i className="fas fa-check-circle"></i>
                  <div>
                    <strong>All rows passed validation.</strong>
                    <span>You can continue with the import.</span>
                  </div>
                </div>
              ) : (
                <div className="validation-help">
                  Rows with Sales Agent errors can be reassigned using the Sales
                  Agent dropdown. Other validation errors must be discarded
                  before continuing.
                </div>
              )}
              <div className="table-wrapper bulk-validation-table">
                <table>
                  <thead>
                    <tr>
                      <th>Excel Row</th>
                      <th>Result</th>
                      <th>Reason for Failure</th>
                      <th>Sales Agent</th>
                      <th>Next Activity</th>
                      <th>Ignore</th>
                    </tr>
                  </thead>
                  <tbody>
                    {validationRows.map((row) => (
                      <tr
                        key={row.row}
                        className={row.valid ? "row-valid" : "row-error"}
                      >
                        <td>{row.row}</td>
                        <td>
                          {row.valid ? (
                            <span className="validation-ok">Valid</span>
                          ) : (
                            <span className="validation-fail">Error</span>
                          )}
                        </td>
                        <td>
                          {row.valid
                            ? "Ready to import"
                            : row.errors.join("; ")}
                        </td>
                        <td>
                          {isAgentError(row) ? (
                            <select
                              className="form-control bulk-agent-select"
                              value={agentOverrides[row.row] || ""}
                              onChange={(e) =>
                                setAgentOverride(row.row, e.target.value)
                              }
                              disabled={ignoredRows.has(row.row)}
                            >
                              <option value="">Use logged-in user</option>

                              {(options.salesReps || []).map((rep) => (
                                <option key={rep.user_id} value={rep.user_id}>
                                  {rep.full_name}
                                  {rep.role ? ` (${rep.role})` : ""}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <span className="text-muted">—</span>
                          )}
                        </td>
                        <td>
                          {row.conflict?.type === "NEXT_ACTIVITY_EXISTS" ? (
                            <div>
                              <div
                                className="next-activity-supersede-info"
                                style={{ marginBottom: 6, fontSize: 12 }}
                              >
                                <strong>
                                  {row.conflict.existing.activity_type}
                                </strong>
                                {" · "}
                                {formatInUserTimezone(
                                  row.conflict.existing.planned_date,
                                )}
                                {row.conflict.existing.notes
                                  ? ` · "${row.conflict.existing.notes}"`
                                  : ""}
                              </div>
                              <select
                                className="form-control bulk-agent-select"
                                value={nextActivityConflicts[row.row] || "skip"}
                                onChange={(e) =>
                                  setNextActivityConflicts((p) => ({
                                    ...p,
                                    [row.row]: e.target.value,
                                  }))
                                }
                                disabled={ignoredRows.has(row.row)}
                              >
                                <option value="skip">
                                  Skip — keep existing
                                </option>
                                <option value="keep_existing">
                                  Keep existing (drop import)
                                </option>
                                <option value="suspend_and_replace">
                                  Suspend existing &amp; replace
                                </option>
                                <option value="complete_and_replace">
                                  Complete existing &amp; replace
                                </option>
                              </select>
                            </div>
                          ) : (
                            <span className="text-muted">—</span>
                          )}
                        </td>
                        <td>
                          {!row.valid && (
                            <input
                              type="checkbox"
                              checked={ignoredRows.has(row.row)}
                              onChange={() => toggleIgnore(row.row)}
                              aria-label={`Ignore Excel row ${row.row}`}
                            />
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {errors.length > 0 && (
                <div className="bulk-error-banner">
                  {errors.map((e, i) => (
                    <div key={i}>{typeof e === "string" ? e : e.error}</div>
                  ))}
                </div>
              )}
            </div>
          )}

          {step === "importing" && (
            <div className="bulk-import-progress">
              <div className="progress-bar">
                <div
                  className="progress-fill"
                  style={{ width: `${progress}%` }}
                ></div>
              </div>
              <p>{progress}% complete — importing validated records...</p>
            </div>
          )}

          {step === "done" && summary && (
            <div className="bulk-import-done">
              <div className="success-icon">
                <i className="fas fa-check-circle"></i>
              </div>
              <h4>Import Completed</h4>
              <div className="import-summary">
                <p>
                  Total XLS records: <strong>{summary.total}</strong>
                </p>
                <p>
                  Successfully imported: <strong>{summary.imported}</strong>
                </p>
                <p>
                  Failed: <strong>{summary.failed}</strong>
                </p>
                <p>
                  Skipped: <strong>{summary.skipped || 0}</strong>
                </p>
              </div>
              {summary.errors?.length > 0 && (
                <>
                  <div className="bulk-error-banner">
                    <strong>Row notes</strong>
                    {summary.errors.map((e, i) => (
                      <div key={i}>
                        Excel Row {e.row ?? "—"}: {e.error}
                      </div>
                    ))}
                  </div>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={downloadFailedRecords}
                  >
                    Download Failed Record Details
                  </button>
                </>
              )}
              <div style={{ marginTop: 20 }}>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={clearFileAndStartOver}
                >
                  Import More
                </button>
              </div>
            </div>
          )}
        </div>

        {step !== "validating" && (
          <div className="bulk-import-footer">
            <div className="bulk-import-footer-left">
              {step !== "upload" && step !== "importing" && (
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={goBack}
                >
                  ← Back
                </button>
              )}
              {step !== "done" && (
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={step === "upload" ? onCancel : clearFileAndStartOver}
                  disabled={importing}
                >
                  {step === "upload" ? "Cancel" : "Start Over"}
                </button>
              )}
            </div>
            <div className="bulk-import-footer-right">
              {step === "upload" && (
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => file && setStep("mapping")}
                  disabled={!file}
                >
                  Next: Map Columns →
                </button>
              )}
              {step === "mapping" && (
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={validateBeforeImport}
                  disabled={importing}
                >
                  Next: Validate {allRows.length} Leads →
                </button>
              )}
              {step === "validation" && (
                <>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={discardInvalidRows}
                    disabled={!validationErrors.length}
                  >
                    Discard Invalid Records
                  </button>

                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={startImport}
                    disabled={importing || blockingErrors.length > 0}
                  >
                    Proceed with Import
                  </button>
                </>
              )}
              {step === "done" && (
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={
                    onComplete
                      ? () => onComplete(summary)
                      : clearFileAndStartOver
                  }
                >
                  Done
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default BulkImport;
